import React, { useState, useEffect } from 'react';

import PostList from '../PostList/PostList';
import ArticleLoader from '../ArticleLoader/ArticleLoader';
import ArticleBanner from '../Banners/ArticleBanner/ArticleBanner';
import styles from './ArticlePosts.module.scss';

import { HTTPClient } from '../../lib/api';
import { GTM_CLASS } from '../../lib/constant';
import { IPost, IPageInfo, IPosts, IInArticleBanners } from '../../lib/types';
import classNames from 'classnames';
import { useRouter } from 'next/router';

interface IProps {
  posts: IPosts;
  category?: string;
  keyword?: string;
  authorName?: string;
  tag?: string;
  emptyMessage?: string;
  inArticleBanners?: IInArticleBanners[];
}

const ArticlePosts = ({
  posts,
  category,
  keyword,
  authorName,
  tag,
  emptyMessage,
  inArticleBanners,
}: IProps) => {
  const [articleList, setArticleList] = useState<{ node: IPost }[]>([]);
  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    endCursor: '',
    hasNextPage: false,
    hasPreviousPage: false,
    startCursor: '',
  });
  const [isFetchingList, setFetchingList] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const BANNER_PER_ARTICLE = 4;
  let counter = 0;
  const router = useRouter();

  const fetchMoreList = async (pos: string, cursor) => {
    setFetchingList(true);

    setTimeout(() => {
      const firsLoader = document.getElementById('loader_0');
      if (firsLoader) window.scrollTo({ top: firsLoader.offsetTop, behavior: 'smooth' });
    }, 100);

    const URL = '/media/api/cms/posts/';
    const req = {
      body: JSON.stringify({
        ...(pos == 'ec' && { first: 20 }),
        ...(pos == 'sc' && { last: 20 }),
        ...(pos == 'ec' && { after: cursor }),
        ...(pos == 'sc' && { before: cursor }),
        ...(category && { category }),
        ...(keyword && { keyword }),
        ...(authorName && { authorName }),
        ...(tag && { tag }),
      }),
      method: 'POST',
    };

    const articlePosts = await HTTPClient(URL, req);

    setFetchingList(false);
    if (articlePosts.posts) {
      if (articlePosts.posts.pageInfo) setPageInfo(articlePosts.posts.pageInfo);
      if (articlePosts.posts.edges && articlePosts.posts.edges.length) {
        setArticleList(articlePosts.posts.edges);
      }
    }
  };

  useEffect(() => {
    if (router?.query.ec) {
      fetchMoreList('ec', router?.query.ec);
    } else if (router?.query.sc) {
      fetchMoreList('sc', router?.query.sc);
    } else {
      if(posts) {
        if (posts.pageInfo) setPageInfo(posts.pageInfo);
        if (posts.edges && posts.edges.length) setArticleList(posts.edges);
      }
    }
  }, [posts, router?.query]);

  const showInarticleBanner = (articleIdxPos) => {
    let articleIdx = articleIdxPos / BANNER_PER_ARTICLE;
    articleIdx = Math.round(articleIdx % inArticleBanners.length);
    articleIdx -= 1;
    return (
      <div className={styles['article__posts--banner']}>
        <ArticleBanner
          data={inArticleBanners[articleIdx]}
          categorySlug={category}
          type={inArticleBanners[articleIdx].type}
        />
      </div>
    );
  };

  const handleRedirect = (pageInfo, pos) => {
    if (pos === 'left') {
      setCurrentPage(currentPage - 1);
    }

    if (pos === 'right') {
      setCurrentPage(currentPage + 1);
    }

    if (currentPage < 2) {
      switch (pos) {
        case 'prev':
          setCurrentPage(1);
          break;
        case 'curr':
          setCurrentPage(2);
          break;
        case 'next':
          setCurrentPage(3);
          break;
        default :
          break;
      }      
    } else {
      switch (pos) {
        case 'prev':
          setCurrentPage(currentPage-1);
          break;
        case 'curr':
          setCurrentPage(currentPage);
          break;
        case 'next':
          setCurrentPage(currentPage+1);
          break;
        default :
          break;
      }
    }
    
    delete router.query.ec;
    delete router.query.sc;
    if ((pos == 'curr' && currentPage == 1) || (pos == 'next' && currentPage > 1) || pos === 'right'){
      router.push({
        pathname: router.pathname,
        query: { ...router.query, ec: pageInfo.endCursor }
      });
    } else if ((pos === 'prev' || pos === 'left') && currentPage == 2) {
      router.push({ 
        pathname: router.pathname,
        query: { ...router.query }}, 
        undefined, 
        {}
      );
    } else {
      router.push({
        pathname: router.pathname,
        query: { ...router.query, sc: pageInfo.startCursor }
      });
    }
  }

  return (
    <div className={styles.article__posts}>
      {articleList.length ? (
        articleList.map((article, index) => {
          const post = article.node;
          if (counter === 3 && inArticleBanners && inArticleBanners.length > 0) {
            counter = 0;
            return (
              <div key={post.slug}>
                <PostList post={post} />
                {showInarticleBanner(index)}
              </div>
            );
          }

          counter++;
          return <PostList post={post} key={post.slug} />;
        })
      ) : (
        <div>{emptyMessage || 'Artikel tidak ditemukan'}</div>
      )}
      {isFetchingList && [...new Array(10)].map((_, i) => <ArticleLoader key={i} index={i} />)}
      {pageInfo && Object.keys(pageInfo).length && (
        <div className={styles['article__posts--footer']}>
          {pageInfo.hasNextPage && (
            <div className={styles['article__posts--pagination']}>
              { currentPage > 1 &&
                <span className={classNames(
                  styles['article__posts--pagination-pagenumbers'], 
                  styles['article__posts--cursor'],                
                  GTM_CLASS.VIEW_MORE_ARTICLE)}
                  onClick={() => { handleRedirect(pageInfo, 'left') }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                    <path d="M13.4922 16.0651L7.65344 10.2318L13.4922 4.39844" stroke="#00274B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                  </svg>
                </span>
              }
              { currentPage > 2 &&
                <span className={styles['article__posts--pagination-pagenumbers']}>…</span>
              }
              <span className={
                classNames(
                  styles['article__posts--pagination-pagenumbers'], 
                  styles['article__posts--cursor'], 
                  currentPage == 1 && styles['article__posts--pagination-current'],
                  GTM_CLASS.VIEW_MORE_ARTICLE
                )} 
                onClick={() => handleRedirect(pageInfo, 'prev')}>
                  {currentPage < 2 ? 1 : (currentPage-1)}
              </span>
              <span className={
                classNames(
                  styles['article__posts--pagination-pagenumbers'], 
                  styles['article__posts--cursor'], 
                  currentPage >= 2 && styles['article__posts--pagination-current'],                
                  GTM_CLASS.VIEW_MORE_ARTICLE)} 
                onClick={() => { handleRedirect(pageInfo, 'curr') }}>
                  {currentPage < 2 ? 2 : currentPage}
              </span>
              <span className={
                classNames(
                  styles['article__posts--pagination-pagenumbers'], 
                  styles['article__posts--cursor'],                
                  GTM_CLASS.VIEW_MORE_ARTICLE)} 
                onClick={() => { handleRedirect(pageInfo, 'next') }}>
                  {currentPage < 2 ? 3 : currentPage+1}
              </span>
              <span className={styles['article__posts--pagination-pagenumbers']}>…</span>
              <span className={classNames(
                styles['article__posts--pagination-pagenumbers'], 
                styles['article__posts--cursor'],              
                GTM_CLASS.VIEW_MORE_ARTICLE)}
                onClick={() => { handleRedirect(pageInfo, 'right') }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                  <path d="M7.50781 4.39779L13.3466 10.2311L7.50781 16.0645" stroke="#00274B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                </svg>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ArticlePosts;
